import { useEffect, useRef } from "react";
import { useU2000WebSocketContext } from "../../../../u2000Core/context/websocket/U2000WebSocketContext";
import {
  isNotificationMessage,
  U3141_FETCH_PROPERTY_INDEX_JOB_TYPE,
  U3141_IMPORT_FROM_LAND_REGISTER_JOB_TYPE,
} from "../../../../u2000Core/types/U2000WebSocketMessages";

/** jobTypes qui déclenchent le refetch du lot enfant */
const U3141_CHILD_LOT_JOB_TYPES = [U3141_FETCH_PROPERTY_INDEX_JOB_TYPE, U3141_IMPORT_FROM_LAND_REGISTER_JOB_TYPE];

/**
 * Écoute les notifications WebSocket "notification:new" pour les jobs U3141 fetch property index / import registre.
 * Lorsque metadata.jobType correspond et metadata.masterLotChainId + childLotChainId
 * correspondent au lot enfant courant, appelle onFetchCompleted pour rafraîchir le lot enfant.
 */
export function useU3141FetchPropertyIndexNotification(
  masterLotChainId: string | undefined,
  childLotChainId: string | undefined,
  onFetchCompleted: (masterLotChainId: string, childLotChainId: string) => void
) {
  const { on, off } = useU2000WebSocketContext();
  const onCompletedRef = useRef(onFetchCompleted);
  onCompletedRef.current = onFetchCompleted;

  useEffect(() => {
    if (!masterLotChainId || !childLotChainId) return;

    const handleNotification = (message: unknown) => {
      if (!isNotificationMessage(message)) return;

      const metadata = message.metadata as Record<string, unknown> | undefined;
      const jobType = metadata?.jobType as string | undefined;
      const masterLotChainIdFromMsg = metadata?.masterLotChainId as string | undefined;
      const childLotChainIdFromMsg = metadata?.childLotChainId as string | undefined;

      if (
        typeof jobType === "string" &&
        U3141_CHILD_LOT_JOB_TYPES.includes(jobType) &&
        masterLotChainIdFromMsg === masterLotChainId &&
        childLotChainIdFromMsg === childLotChainId
      ) {
        if (import.meta.env.DEV) {
          console.log("[U3141 Fetch Property Index] Notification reçue, refetch childLotChain:", masterLotChainId, childLotChainId);
        }
        onCompletedRef.current(masterLotChainId, childLotChainId);
      }
    };

    on("notification:new", handleNotification);
    return () => off("notification:new", handleNotification);
  }, [masterLotChainId, childLotChainId, on, off]);
}
